import React, { FC, useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { DialogFooterProps, PICKER_TYPES } from '../../PhotoPickerDialog.types';

export const DialogFooter: FC<DialogFooterProps> = props => {
  const {
    handleSelectAll,
    handleDeselect,
    handleCancel,
    handleAdd,
    isAdding,
    disableAddCTA,
    disableDeselectAllCTA = false,
    disableSelectAllCTA = false,
    pickerType = PICKER_TYPES.ADD_TO_COLLECTION,
    selectedItemsCount,
  } = props;

  const { t } = useTranslation('websiteOverview');
  const photosPickerTranslations = t('photos', 'photoPickerDialog');

  const [isMobile] = useResponsive({ values: { xxs: true, sm: false } });

  const renderCTALabel = useCallback(() => {
    if (pickerType === PICKER_TYPES.ADD_TO_COLLECTION) {
      return isMobile ? photosPickerTranslations.add() : photosPickerTranslations.addToAlbum();
    }
    return selectedItemsCount && selectedItemsCount > 1
      ? photosPickerTranslations.selectPhotosCount({ count: selectedItemsCount })
      : photosPickerTranslations.selectPhotoCount({ count: selectedItemsCount });
  }, [isMobile, photosPickerTranslations, pickerType, selectedItemsCount]);

  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      width="100%"
    >
      <Flex
        flexDirection="row"
        gap={3}
      >
        {pickerType === PICKER_TYPES.ADD_TO_COLLECTION && (
          <>
            {handleSelectAll && (
              <ButtonV2
                size="lg"
                variant="ghost"
                shape="rounded"
                intent="neutral"
                onClick={handleSelectAll}
                disabled={disableSelectAllCTA}
              >
                {photosPickerTranslations.selectAll()}
              </ButtonV2>
            )}
            {handleDeselect && (
              <ButtonV2
                size="lg"
                variant="ghost"
                shape="rounded"
                intent="neutral"
                onClick={handleDeselect}
                disabled={disableDeselectAllCTA}
              >
                {photosPickerTranslations.deselectAll()}
              </ButtonV2>
            )}
          </>
        )}
      </Flex>
      <Flex
        flexDirection="row"
        gap={3}
      >
        {pickerType === PICKER_TYPES.ADD_TO_COLLECTION && (
          <ButtonV2
            size="lg"
            display={isMobile ? 'none' : 'inline-flex'}
            variant="ghost"
            shape="rounded"
            intent="neutral"
            onClick={handleCancel}
            disabled={isAdding}
          >
            {photosPickerTranslations.cancel()}
          </ButtonV2>
        )}
        <ButtonV2
          size="lg"
          variant="solid"
          shape="rounded"
          intent="neutral"
          onClick={handleAdd}
          minWidth={isMobile ? pxToRem(80) : pxToRem(150)}
          disabled={disableAddCTA || isAdding}
          loading={isAdding}
        >
          {renderCTALabel()}
        </ButtonV2>
      </Flex>
    </Flex>
  );
};

DialogFooter.displayName = 'DialogFooter';
